import React, { useEffect, useRef, useState, useCallback } from 'react';
import { StatelessDemoTrace, DemoStep } from '../shared/statelessDemoTrace';
import { BlogFigurePlayButton } from './BlogFigureControls';

type Phase = 'observe' | 'think' | 'parked' | 'waking' | 'act' | 'idle';

function phaseAt(steps: DemoStep[], t: number): { phase: Phase; step: number } {
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (t >= s.observeStart && t < s.thinkStart) return { phase: 'observe', step: i };
    if (t >= s.thinkStart && t < s.parkStart) return { phase: 'think', step: i };
    if (t >= s.parkStart && t < s.wakeStart) return { phase: 'parked', step: i };
    if (t >= s.wakeStart && t < s.wakeReady) return { phase: 'waking', step: i };
    if (t >= s.wakeReady && t < s.act + 180) return { phase: 'act', step: i };
  }
  return { phase: 'idle', step: -1 };
}

// The guest clock freezes while parked and is repaired to real time on wake.
function guestClockMs(steps: DemoStep[], t: number): number {
  for (const s of steps) {
    if (t >= s.parkStart && t < s.wakeStart) return s.parkStart;
  }
  return t;
}

function repairInfo(steps: DemoStep[], t: number): number | null {
  for (const s of steps) {
    if (t >= s.wakeStart && t < s.wakeStart + 1100) return s.wakeStart - s.parkStart;
  }
  return null;
}

function sampleAt(series: { t: number; v: number }[], t: number): number {
  if (!series.length) return 0;
  let lo = 0, hi = series.length - 1;
  if (t <= series[0].t) return series[0].v;
  if (t >= series[hi].t) return series[hi].v;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (series[mid].t <= t) lo = mid; else hi = mid;
  }
  return series[lo].v;
}

function currentFrame(trace: StatelessDemoTrace, t: number) {
  let f = trace.frames[0];
  for (const fr of trace.frames) { if (fr.t <= t) f = fr; else break; }
  return f;
}

// Reserved layout for the status chip. Fixed so the figure never changes
// height or shifts its text when the phase changes.
const CHIP_ROW_H = 28;
const CHIP_W = 330;
const CHIP_W_COMPACT = 210;
const READOUT_H = 34;

const BASE_CLOCK_S = 9 * 3600 + 41 * 60; // display as a wall clock ~09:41
function fmtClock(ms: number): string {
  const total = BASE_CLOCK_S + Math.floor(ms / 1000);
  const h = Math.floor(total / 3600) % 24;
  const m = Math.floor(total / 60) % 60;
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

const SessionReplay: React.FC<{ trace: StatelessDemoTrace }> = ({ trace }) => {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(
    () => typeof window === 'undefined' || !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [width, setWidth] = useState(760);
  const [imgOk, setImgOk] = useState(true);
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const rafRef = useRef<number>();
  const lastRef = useRef<number>();
  const scrubRef = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(Math.max(320, e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) {
      lastRef.current = undefined;
      return;
    }

    const step = (now: number) => {
      if (lastRef.current == null) lastRef.current = now;
      const dt = now - lastRef.current;
      lastRef.current = now;
      if (!scrubRef.current) {
        setT((prev) => (prev + dt) % trace.durationMs);
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); lastRef.current = undefined; };
  }, [playing, trace.durationMs]);

  const scrubTo = useCallback((clientX: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setT(frac * trace.durationMs);
  }, [trace.durationMs]);

  const { phase } = phaseAt(trace.steps, t);
  // Phone-width figure: the status pills need shorter copy to stay on one line.
  const compact = width < 480;
  const parked = phase === 'parked';
  const residentMB = sampleAt(trace.hostResidentMB, t);
  const clockMs = guestClockMs(trace.steps, t);
  const repaired = repairInfo(trace.steps, t);
  const frame = currentFrame(trace, t);

  // Timeline geometry. One lane only: host RAM. The agent-phase strip and the
  // S3 lane were removed to keep this a mechanism illustration rather than a
  // telemetry dashboard, and because this trace is a tmpfs pause with no
  // durable demotion.
  const padL = 96, padR = 12, padT = 10;
  const innerW = width - padL - padR;
  const x = (ms: number) => padL + (ms / trace.durationMs) * innerW;
  const memY = padT, memH = 74;
  // Room under the lane for the bracket spanning the park window.
  const brkY = memY + memH + 16;
  const H = brkY + 22;

  // Label the longest park rather than a fixed index: the short parks on either
  // side carry the cadence, and this one carries the measurement.
  const park = trace.steps.reduce((longest, s) =>
    s.wakeStart - s.parkStart > longest.wakeStart - longest.parkStart ? s : longest
  );
  const parkSeconds = ((park.wakeStart - park.parkStart) / 1000).toFixed(1);

  const memPts = trace.hostResidentMB.map((s) => `${x(s.t)},${memY + memH - (s.v / trace.hostResidentMaxMB) * memH}`).join(' ');
  const memArea = `${padL},${memY + memH} ${memPts} ${padL + innerW},${memY + memH}`;
  const playX = x(t);

  // One chip, three states. Keeping a single element (hidden when idle) means
  // the reserved space never depends on which phase is showing.
  const chip = parked
    ? { fill: '#D1C4E9', stroke: '#512DA8', ink: '#311B92', label: compact ? 'parked · slot released' : 'parked · VM snapshotted, host slot released' }
    : repaired != null
      ? { fill: '#C8E6C9', stroke: '#2E7D32', ink: '#1B5E20', label: `clock repaired +${(repaired / 1000).toFixed(1)}s on wake` }
      : { fill: 'transparent', stroke: 'transparent', ink: 'transparent', label: '' };

  const labelStyle: React.CSSProperties = { fontSize: 11, fill: '#78716c', fontFamily: 'inherit' };

  return (
    <figure ref={wrapRef} className="my-8 reading-figure" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
      <div className="blog-figure-frame" style={{ overflow: 'hidden' }}>
        {/* Guest clock panel. Deliberately on the same light ground as the
            other figures in the post rather than a faux terminal window. */}
        <div style={{ position: 'relative', padding: compact ? '18px 12px 16px' : '22px 16px 18px', borderBottom: '1px solid #e7e5e4', background: '#fff' }}>
          {frame.file && imgOk ? (
            <img
              src={frame.file}
              alt="desktop"
              onError={() => setImgOk(false)}
              style={{ display: 'block', width: '100%', height: 190, objectFit: 'cover', borderRadius: 8, opacity: parked ? 0.4 : 1, filter: parked ? 'grayscale(1)' : 'none', transition: 'opacity 140ms, filter 140ms' }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'opacity 140ms', opacity: parked ? 0.45 : 1 }}>
              <div style={{ fontSize: 10.5, letterSpacing: '0.09em', color: '#78716c', textTransform: 'uppercase' }}>Desktop sandbox clock</div>
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: compact ? 38 : 52, lineHeight: 1.05, fontWeight: 500, color: '#1c1917', letterSpacing: 2 }}>
                {fmtClock(clockMs)}
              </div>
            </div>
          )}

          {/* Status chip. Both variants share one shape, one width and one
              text origin, and the row is a fixed height, so nothing in the
              figure reflows as the phase changes. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, height: CHIP_ROW_H }}>
            <span
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                width: compact ? CHIP_W_COMPACT : CHIP_W,
                maxWidth: '100%',
                boxSizing: 'border-box',
                padding: '3px 10px',
                borderRadius: 99,
                border: `1px solid ${chip.stroke}`,
                background: chip.fill,
                color: chip.ink,
                fontSize: 11.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                visibility: chip.label ? 'visible' : 'hidden',
              }}
            >
              <span style={{ width: '100%', overflow: 'hidden', textAlign: 'center', textOverflow: 'ellipsis' }}>
                {chip.label}
              </span>
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div style={{ padding: '12px 14px 8px', background: '#fafaf9' }}>
          <svg
            ref={svgRef}
            width="100%"
            height={H}
            viewBox={`0 0 ${width} ${H}`}
            style={{ cursor: 'ew-resize', display: 'block', touchAction: 'none' }}
            onPointerDown={(e) => { scrubRef.current = true; (e.target as Element).setPointerCapture?.(e.pointerId); scrubTo(e.clientX); }}
            onPointerMove={(e) => { if (scrubRef.current) scrubTo(e.clientX); }}
            onPointerUp={(e) => { scrubRef.current = false; (e.target as Element).releasePointerCapture?.(e.pointerId); }}
            onPointerLeave={() => { scrubRef.current = false; }}
          >
            {/* lane labels */}
            <text x={padL - 8} y={memY + 11} textAnchor="end" style={labelStyle}>host RAM</text>
            <text x={padL - 8} y={memY + 24} textAnchor="end" style={{ ...labelStyle, fontSize: 9, fill: '#a8a29e' }}>{trace.hostResidentMaxMB}MB</text>
            <text x={padL - 8} y={memY + memH + 3} textAnchor="end" style={{ ...labelStyle, fontSize: 9, fill: '#a8a29e' }}>0</text>

            {/* host memory area */}
            <polyline points={memArea} fill="rgba(96,165,250,0.18)" stroke="none" />
            <polyline points={memPts} fill="none" stroke="#3b82f6" strokeWidth={2} />
            {/* zero baseline */}
            <line x1={padL} y1={memY + memH} x2={padL + innerW} y2={memY + memH} stroke="#e7e5e4" strokeWidth={1} />

            {/* bracket marking the one park window */}
            <line x1={x(park.parkStart)} y1={brkY} x2={x(park.wakeStart)} y2={brkY} stroke="#8b5cf6" strokeWidth={1.5} />
            <line x1={x(park.parkStart)} y1={brkY - 4} x2={x(park.parkStart)} y2={brkY + 4} stroke="#8b5cf6" strokeWidth={1.5} />
            <line x1={x(park.wakeStart)} y1={brkY - 4} x2={x(park.wakeStart)} y2={brkY + 4} stroke="#8b5cf6" strokeWidth={1.5} />
            <text
              x={(x(park.parkStart) + x(park.wakeStart)) / 2}
              y={brkY + 16}
              textAnchor="middle"
              style={{ fontSize: 11.5, fill: '#6d28d9', fontFamily: 'inherit' }}
            >
              {parkSeconds}s off-host · slot released
            </text>

            {/* playhead */}
            <line x1={playX} y1={memY} x2={playX} y2={memY + memH} stroke="#1c1917" strokeWidth={1.5} />
            <circle cx={playX} cy={memY} r={3.5} fill="#1c1917" />
          </svg>

          {/* Balanced telemetry row: RAM and slot occupancy sit on either side
              of a playback control that remains locked to the true center. */}
          <div style={{ position: 'relative', height: READOUT_H, fontSize: 13 }}>
            <span style={{ position: 'absolute', top: '50%', left: '25%', width: 122, color: '#57534e', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', textAlign: 'center', transform: 'translate(-50%, -50%)' }}>
              host RAM:{' '}
              <b style={{ color: residentMB === 0 ? '#16a34a' : '#1c1917' }}>
                {residentMB} MB
              </b>
            </span>
            <BlogFigurePlayButton
              className="session-replay__playback"
              playing={playing}
              onClick={() => setPlaying((p) => !p)}
              label="replay"
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: '75%',
                width: 88,
                color: residentMB === 0 ? '#237132' : '#5b3b78',
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.2,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {residentMB === 0 ? 'slot free' : 'slot in use'}
            </span>
          </div>
        </div>
      </div>
      <figcaption className="reading-figure__caption">{trace.caption}</figcaption>
    </figure>
  );
};

export default SessionReplay;
